import { Edge, Node } from '@xyflow/react';
import {
  NodeCategoryModel,
  ERedundancyProtocol,
} from 'src/modules/network-configuration/models/node-data';
import { IConfigurationStp } from 'src/types/configuration';

// Helper function to detect loops in topology
const detectLoopsInTopology = (nodes: Node<any>[], edges: Edge[]): boolean => {
  // Create adjacency list representation of the graph
  const adjacencyList = new Map<string, string[]>();

  // Initialize adjacency list for all nodes
  nodes.forEach((node) => {
    adjacencyList.set(node.id, []);
  });

  // Build adjacency list from edges
  edges.forEach((edge) => {
    const { source, target } = edge;

    if (adjacencyList.has(source)) {
      adjacencyList.get(source)!.push(target);
    }
    if (adjacencyList.has(target)) {
      adjacencyList.get(target)!.push(source);
    }
  });

  // DFS function to detect cycles
  const hasCycle = (
    nodeId: string,
    visited: Set<string>,
    parent: string | null,
  ): boolean => {
    visited.add(nodeId);

    const neighbors = adjacencyList.get(nodeId) || [];
    let hasCycleFound = false;

    neighbors.forEach((neighbor) => {
      if (!visited.has(neighbor)) {
        if (hasCycle(neighbor, visited, nodeId)) {
          hasCycleFound = true;
        }
      } else if (neighbor !== parent) {
        // If neighbor is visited and not the parent, we found a back edge (cycle)
        hasCycleFound = true;
      }
    });

    return hasCycleFound;
  };

  // Check for cycles starting from each unvisited node
  const visited = new Set<string>();
  let loopDetected = false;

  nodes.forEach((node) => {
    if (!visited.has(node.id) && !loopDetected) {
      if (hasCycle(node.id, visited, null)) {
        loopDetected = true; // Loop detected
      }
    }
  });

  return loopDetected; // No loops found
};

export const configurationSTP = ({
  nodeCategory,
  nodes,
  edges,
}: {
  nodeCategory: NodeCategoryModel;
  nodes: Node<any>[];
  edges: Edge[];
}): IConfigurationStp => {
  const stpSettings = nodeCategory.stp;

  let lan = 'rstp';
  let lanprio = 8;

  // Handle Redundancy Protocol
  switch (stpSettings?.redundancyProtocol) {
    case ERedundancyProtocol.DISABLED:
      lan = 'none';
      break;
    case ERedundancyProtocol.RSTP:
      lan = 'rstp';
      break;
    case ERedundancyProtocol.AUTO:
    default: {
      // Auto-detect based on topology: if no loops -> disabled, otherwise STP
      const hasLoops = detectLoopsInTopology(nodes, edges);
      lan = hasLoops ? 'rstp' : 'none';
      break;
    }
  }

  // Handle Bridge Priority
  if (
    stpSettings &&
    stpSettings.bridgePriority >= 0 &&
    stpSettings.bridgePriority <= 15
  ) {
    lanprio = stpSettings.bridgePriority;
  }

  return {
    stpVersion: {
      lan,
      lanprio,
    },
    version: 1,
  };
};
